import React, { useState } from "react";
import { useList, useInvalidate } from "@/lib/hooks";
import api from "@/lib/api";
import { PageHeader, Panel, EmptyState, Field, money } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Coins, CheckCircle, Warning, Trash } from "@phosphor-icons/react";
import { PAYMENT_METHODS, INVOICE_PRODUCTS } from "@/lib/constants";
import { toast } from "sonner";

const BUCKETS = [
  { key: "current", label: "Current", tone: "text-emerald-600" },
  { key: "1-30", label: "1-30 days", tone: "text-amber-600" },
  { key: "31-60", label: "31-60 days", tone: "text-orange-600" },
  { key: "60+", label: "60+ days", tone: "text-red-600" },
];

export default function Receivable() {
  const { data, isLoading } = useList("ar", "/accounting/receivable");
  const { data: byProduct } = useList("ar-by-product", "/accounting/by-product");
  const invalidate = useInvalidate();
  const rows = data?.rows || [];
  const prodRows = (byProduct?.rows || []).filter((r) => r.total > 0 || r.product !== "Other");
  const [payInv, setPayInv] = useState(null);
  const [method, setMethod] = useState("ACH");

  const confirmPaid = async () => {
    await api.put(`/invoices/${payInv.id}`, { status: "paid", payment_method: method });
    toast.success(`Invoice marked paid · ${method}`);
    setPayInv(null);
    invalidate(["ar", "ar-by-product", "invoices", "accounting-summary", "carriers"]);
  };

  const removeInv = async (r) => {
    if (!window.confirm(`Delete invoice ${r.number}? This cannot be undone.`)) return;
    try {
      await api.delete(`/invoices/${r.id}`);
      toast.success("Invoice deleted");
      invalidate(["ar", "ar-by-product", "invoices", "accounting-summary", "ap", "carriers"]);
    } catch (e) { toast.error("Could not delete invoice"); }
  };

  return (
    <div>
      <PageHeader title="Accounts Receivable" subtitle="Outstanding dispatch invoices owed to you, by age" />

      <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">Revenue by Product</p>
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 mb-5">
        {INVOICE_PRODUCTS.map((p) => {
          const r = prodRows.find((x) => x.product === p) || { collected: 0, outstanding: 0, total: 0, count: 0 };
          return (
            <Panel key={p} className="p-4" data-testid={`product-${p.replace(/\s+/g, "-")}`}>
              <p className="text-sm font-heading font-bold text-slate-900">{p}</p>
              <p className="text-2xl font-heading font-extrabold text-blue-600 tabular mt-1">{money(r.total)}</p>
              <div className="flex gap-4 mt-2 text-xs">
                <span className="text-emerald-600">Collected {money(r.collected)}</span>
                <span className="text-amber-600">Open {money(r.outstanding)}</span>
              </div>
            </Panel>
          );
        })}
      </div>

      <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">Outstanding by Age</p>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
        <Panel className="p-4"><p className="text-xs text-slate-500">Total Outstanding</p><p className="text-2xl font-heading font-extrabold text-blue-600 tabular">{money(data?.total)}</p></Panel>
        {BUCKETS.map((b) => (
          <Panel key={b.key} className="p-4"><p className="text-xs text-slate-500">{b.label}</p><p className={`text-2xl font-heading font-extrabold tabular ${b.tone}`}>{money(data?.buckets?.[b.key])}</p></Panel>
        ))}
      </div>
      <Panel className="p-0">
        {isLoading ? <p className="p-6 text-slate-400">Loading…</p> : rows.length === 0 ? (
          <EmptyState icon={CheckCircle} title="All caught up" hint="No outstanding receivables — every invoice is paid." />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-200 hover:bg-transparent">
                  <TableHead>Invoice</TableHead><TableHead>Customer</TableHead><TableHead>Issue</TableHead><TableHead>Due</TableHead><TableHead>Age</TableHead><TableHead>Amount</TableHead><TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} className="border-slate-100" data-testid={`ar-row-${r.id}`}>
                    <TableCell className="font-mono font-semibold text-blue-600">{r.number}</TableCell>
                    <TableCell className="font-medium text-slate-900">{r.customer}</TableCell>
                    <TableCell className="text-sm text-slate-600">{r.issue_date || "—"}</TableCell>
                    <TableCell className="text-sm text-slate-600">{r.due_date || "—"}</TableCell>
                    <TableCell>{r.bucket === "current" ? <span className="text-xs text-slate-500">Current</span> : <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-600"><Warning size={12} weight="fill" />{r.bucket}</span>}</TableCell>
                    <TableCell className="font-mono font-semibold text-slate-900">{money(r.total)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1.5 items-center">
                        <Button size="sm" onClick={() => { setPayInv(r); setMethod("ACH"); }} className="bg-emerald-600 text-white hover:bg-emerald-700 font-semibold" data-testid={`ar-paid-${r.id}`}>Mark Paid</Button>
                        <button onClick={() => removeInv(r)} className="p-1.5 text-slate-400 hover:text-red-500" title="Delete invoice" data-testid={`ar-delete-${r.id}`}><Trash size={16} /></button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Panel>

      <Dialog open={!!payInv} onOpenChange={(v) => !v && setPayInv(null)}>
        <DialogContent className="bg-white border-slate-200 max-w-sm" data-testid="ar-payment-dialog">
          <DialogHeader><DialogTitle className="font-heading">Record Payment</DialogTitle><DialogDescription className="sr-only">Record payment method</DialogDescription></DialogHeader>
          <p className="text-sm text-slate-500 -mt-1">{payInv?.number} · {payInv?.customer} · <b className="text-slate-900">{money(payInv?.total)}</b></p>
          <div className="py-2">
            <Field label="Payment Method">
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger className="bg-white border-slate-300" data-testid="ar-payment-method"><SelectValue /></SelectTrigger>
                <SelectContent>{PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayInv(null)} className="border-slate-300 bg-white text-slate-700">Cancel</Button>
            <Button onClick={confirmPaid} className="bg-emerald-600 text-white hover:bg-emerald-700 font-semibold" data-testid="ar-confirm-paid">Mark Paid</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
